// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// material ui
import { Button, Menu, MenuItem } from '@mui/material';

// components
import useStableSearchParams from '@/hooks/useStableSearchParams';

/**
 * Compact aggregation selector rendered as a single button with a dropdown menu.
 * Reads/writes selection to the `agg` search param (day|week|month).
 */
const AggregationSelector = ({ param = 'agg' }) => {
  const { t } = useTranslation('dashboards-common');
  const [searchParams, setSearchParams] = useStableSearchParams();
  const current = searchParams.get(param) || 'day';

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const setAgg = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(param, value);
      else next.delete(param);
      return next;
    });
    closeMenu();
  };

  const labelMap = {
    hour: t('aggregation.hour'),
    day: t('aggregation.day'),
    week: t('aggregation.week'),
    month: t('aggregation.month'),
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={openMenu}
        endIcon={<ArrowDropDownIcon />}
        data-testid="agg-button"
        sx={{
          borderRadius: 2,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          textTransform: 'none',
          p: 0.9,
          color: 'text.primary',
        }}
      >
        {labelMap[current] ?? labelMap.week}
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          selected={current === 'hour'}
          onClick={() => setAgg('hour')}
          data-testid="agg-hour"
        >
          {labelMap.hour}
        </MenuItem>
        <MenuItem
          selected={current === 'day'}
          onClick={() => setAgg('day')}
          data-testid="agg-day"
        >
          {labelMap.day}
        </MenuItem>
        <MenuItem
          selected={current === 'week'}
          onClick={() => setAgg('week')}
          data-testid="agg-week"
        >
          {labelMap.week}
        </MenuItem>
        <MenuItem
          selected={current === 'month'}
          onClick={() => setAgg('month')}
          data-testid="agg-month"
        >
          {labelMap.month}
        </MenuItem>
      </Menu>
    </>
  );
};

export default AggregationSelector;
